/**
 * Shared utilities for report Export (CSV) and Print functionality.
 *
 * columns: [{ header: string, key: string, format?: (val, row) => string }]
 * rows: array of data objects
 */

export function exportToCsv(filename, columns, rows) {
  const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const header = columns.map(c => escape(c.header)).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const raw = c.key ? row[c.key] : undefined;
      const val = c.format ? c.format(raw, row) : raw;
      return escape(val == null ? '' : val);
    }).join(',')
  );
  const csv = [header, ...body].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function printReport(title, columns, rows, subtitle = '') {
  const th = columns.map(c => `<th>${c.header}</th>`).join('');
  const tb = rows.map(row =>
    `<tr>${columns.map(c => {
      const raw = c.key ? row[c.key] : undefined;
      const val = c.format ? c.format(raw, row) : raw;
      return `<td>${val == null ? '-' : val}</td>`;
    }).join('')}</tr>`
  ).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; padding: 20px; }
  .report-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; border-bottom: 2px solid #1e3a5f; padding-bottom: 10px; }
  .report-header h1 { font-size: 18px; color: #1e3a5f; }
  .report-header .meta { font-size: 10px; color: #555; text-align: right; line-height: 1.6; }
  .subtitle { font-size: 11px; color: #555; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  thead tr { background: #1e3a5f; color: #fff; }
  thead th { padding: 7px 8px; text-align: left; font-size: 10px; font-weight: bold; letter-spacing: 0.04em; white-space: nowrap; }
  tbody td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  tbody tr:nth-child(even) td { background: #f5f7fb; }
  tfoot tr { background: #e8ecf5; font-weight: bold; }
  tfoot td { padding: 7px 8px; border-top: 2px solid #1e3a5f; }
  .footer { margin-top: 14px; font-size: 9px; color: #888; text-align: right; }
  @page { size: A4 landscape; margin: 12mm; }
  @media print { html, body { height: auto; } }
</style>
</head>
<body>
  <div class="report-header">
    <div>
      <h1>${title}</h1>
      ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}
    </div>
    <div class="meta">
      Rows: ${rows.length}<br/>
      Printed: ${new Date().toLocaleString('en-IN')}
    </div>
  </div>
  <table>
    <thead><tr>${th}</tr></thead>
    <tbody>${tb || '<tr><td colspan="' + columns.length + '" style="text-align:center;color:#999;padding:20px">No data</td></tr>'}</tbody>
  </table>
  <div class="footer">LogisticsHub — Confidential</div>
  <script>window.onload = function () { window.print(); };<\/script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=750');
  if (win) { win.document.write(html); win.document.close(); }
}

/** Reusable ExportPrintBar component for all report tabs */
export function ExportPrintBar({ onExport, onPrint, disabled = false }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onExport}
        disabled={disabled}
        data-testid="export-csv-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-ibm bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export CSV
      </button>
      <button
        onClick={onPrint}
        disabled={disabled}
        data-testid="print-report-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-ibm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        Print
      </button>
    </div>
  );
}
